import { OrganizationalRecordPage } from "@/pages/organizational-record/OrganizationalRecordPage";
import { createBrowserRouter, Navigate } from "react-router";
import { getConfig } from "@/lib/config";
import { NotFoundPage } from "@/pages/NotFoundPage";
import {
  requireAuth,
  requireCompanyCreation,
  requireSystemAccess,
} from "@/auth/require-auth";
import { RequireFeature } from "@/auth/require-feature";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { AppShell } from "@/components/layout/AppShell";
import { RouteErrorBoundary } from "@/components/common/RouteErrorBoundary";
import { OverviewRedirect } from "@/components/common/OverviewRedirect";
import { GrowOverviewRedirect } from "@/components/grow/GrowOverviewRedirect";
import { RetiredGrowRedirect } from "@/components/grow/RetiredGrowRedirect";
import { HomePage } from "@/pages/HomePage";
import { ProfilePage } from "@/pages/save/ProfilePage";
import { CompanyPage } from "@/pages/company/CompanyPage";
import { MyCompaniesPage } from "@/pages/company/MyCompaniesPage";
import { AgentPage } from "@/pages/agent/AgentPage";
import { ServicePage } from "@/pages/save/ServicePage";
import { ContactPage } from "@/pages/save/ContactPage";
import { StudioPage } from "@/pages/studio/StudioPage";
import { PinboardPage } from "@/pages/save/PinboardPage";
import { ElementAssessmentPage } from "@/pages/grow/ElementAssessmentPage";
import { ElementResultPage } from "@/pages/grow/ElementResultPage";
import { GameGroupPage } from "@/pages/grow/GameGroupPage";
import { GamePage } from "@/pages/grow/GamePage";
import { GrowPlayPage } from "@/pages/grow/GrowPlayPage";
import { GrowWatchPage } from "@/pages/grow/GrowWatchPage";
import { PowerDetailPage } from "@/pages/grow/PowerDetailPage";
import { QuestionnaireGroupPage } from "@/pages/grow/QuestionnaireGroupPage";
import { QuestionnaireGroupPartPage } from "@/pages/grow/QuestionnaireGroupPartPage";
import { QuestionnaireGroupResultPage } from "@/pages/grow/QuestionnaireGroupResultPage";
import { AboutPowersPage } from "@/pages/grow/AboutPowersPage";
import { PowerLibraryPage } from "@/pages/grow/PowerLibraryPage";
import { QuestionnaireAssessmentPage } from "@/pages/grow/QuestionnaireAssessmentPage";
import { MatchPage } from "@/pages/match/MatchPage";
import { MatchDetailPage } from "@/pages/match/MatchDetailPage";
import { SharePage } from "@/pages/share/SharePage";
import { ShareCreatePage } from "@/pages/share/ShareCreatePage";
import { ShareItemDetailPage } from "@/pages/share/ShareItemDetailPage";
import { ShareMinePage } from "@/pages/share/ShareMinePage";
import { ExtendedSearchPage } from "@/pages/ExtendedSearchPage";
import { ProjectMockPage } from "@/pages/project/ProjectMockPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { UserSettingsPage } from "@/pages/UserSettingsPage";
import { LoginPage } from "@/pages/auth/LoginPage";

// React Router data router. Two top-level scopes:
//   /auth/*          - login, callback. No guard.
//   /                - everything else, gated by requireAuth loader.
//
// The basename comes from runtime config so the same build serves
// `/`, `/smycles/`, etc. on different deployments without rebuilding.

export function makeRouter() {
  const cfg = getConfig();
  return createBrowserRouter(
    [
      // --- Auth scope (no guard) ---
      { path: "auth/login", element: <LoginPage /> },
      {
        path: "auth/callback",
        // The actual code exchange happens server-side at the BFF's
        // /auth/callback. If a browser somehow lands here after the
        // BFF already processed the callback, bounce to home.
        element: <Navigate to="/" replace />,
      },

      // --- Protected scope ---
      {
        path: "/",
        loader: requireAuth,
        element: <AppShell />,
        // Outer, shell-level fallback. Only two things reach it: a throw while AppShell itself
        // renders, and a requireAuth loader rejection - whose error is keyed to this match, so the
        // inner boundary cannot see it. Neither could be shown inside the shell anyway.
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            // Pathless layout route: this boundary lives INSIDE AppShell's <Outlet/>, so a throw
            // in a page replaces only the page. Topbar and navigation stay mounted, which is what
            // lets the user leave the broken screen. Putting it on the "/" route instead replaced
            // AppShell itself - React renders the nearest boundary IN PLACE OF that route's element.
            errorElement: <RouteErrorBoundary />,
            children: [
              { index: true, element: <HomePage /> },

              // Old overview links now open the profile; keep the existing feature guard.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.save.key} />,
                children: [{ path: "save", element: <OverviewRedirect to="/save/basics" /> }],
              },
              // UI-only gate (242-access-model), pure spark navigation toggle ahead of a
              // deadline - see Feature.java's class javadoc. No resolver was analysed here.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.saveProfile.key} />,
                children: [{ path: "save/basics", element: <ProfilePage /> }],
              },
              { path: "p/:id", element: <ProfilePage /> },
              { path: "p/:id/organisation", element: <OrganizationalRecordPage /> },
              // The caller's own memberships (the company account model). Open
              // to any authenticated user: being a member takes no platform role, so gating this on
              // create:company would hide their own companies from exactly the people who have one.
              { path: "c", element: <MyCompaniesPage /> },
              // Must stay above the :id route so "new" is not matched as a company id.
              // Gated on the create:company role (level A) — the ONLY thing that role does.
              { path: "c/new", loader: requireCompanyCreation, element: <CompanyPage creating /> },
              { path: "c/:id", element: <CompanyPage /> },
              { path: "c/:id/organisation", element: <OrganizationalRecordPage subjectKind="company" /> },
              // An AI agent of the instance, read-only (spark#1087). Any authenticated caller reads
              // the list in genesis, so no gate beyond the parent's requireAuth.
              { path: "a/:id", element: <AgentPage /> },
              // UI-only gate, same as save.profile above.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.saveDocuments.key} />,
                children: [{ path: "save/documents", element: <ServicePage /> }],
              },
              // UI-only gate (242-access-model): genesis has no server-side guard for
              // save.community, deliberately - the contact list reads the same `persons`
              // resolver global search and match candidate picking use, and gating that
              // resolver would hide the caller's own contacts from those surfaces too. This
              // key only ever decides whether spark offers the route; the data stays fully
              // self-scope reachable everywhere else it is read.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.saveCommunity.key} />,
                children: [{ path: "save/contact", element: <ContactPage /> }],
              },
              // UI-only gate, same as save.profile above.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.savePins.key} />,
                children: [
                  { path: "save/studio", element: <StudioPage /> },
                  { path: "save/pinboard", element: <PinboardPage /> },
                ],
              },

              // Grow, gated on the "grow" feature key (242-access-model). genesis
              // still enforces every query/mutation server-side; RequireFeature only
              // keeps the pages out of reach when the whitelist doesn't cover it.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.grow.key} />,
                children: [
                  { path: "grow", element: <GrowOverviewRedirect /> },
                  // Nested UI-only gate, same reasoning as grow.map below.
                  {
                    element: <RequireFeature featureKey={FEATURE_CATALOG.growWatch.key} />,
                    children: [{ path: "grow/watch", element: <GrowWatchPage /> }],
                  },
                  // Nested UI-only gate, same reasoning as grow.map below. Covers the whole
                  // Play subtree: the group pages and the old Power Quiz deep-link redirects
                  // all lead back to /grow/play, so they gate with it.
                  {
                    element: <RequireFeature featureKey={FEATURE_CATALOG.growPlay.key} />,
                    children: [
                      { path: "grow/play", element: <GrowPlayPage /> },
                      {
                        path: "grow/play/group/:questionnaireGroupId",
                        element: <QuestionnaireGroupPage />,
                      },
                      // Must stay above the :questionnaireId route - otherwise "result" is
                      // matched as a questionnaire id.
                      {
                        path: "grow/play/group/:questionnaireGroupId/result",
                        element: <QuestionnaireGroupResultPage />,
                      },
                      {
                        path: "grow/play/group/:questionnaireGroupId/:questionnaireId",
                        element: <QuestionnaireGroupPartPage />,
                      },
                      { path: "grow/play/games/:groupKey", element: <GameGroupPage /> },
                      { path: "grow/play/game/:gameKey", element: <GamePage /> },
                      // Old Power Quiz deep links. They cannot be forwarded to a specific
                      // group: which group the quiz is now lives in the database, and
                      // hard-coding an id here is precisely the assumption spark#196 removes.
                      // The catalogue is one click from the quiz, so that is where they land.
                      {
                        path: "grow/play/power-quiz",
                        element: <Navigate to="/grow/play" replace />,
                      },
                      {
                        path: "grow/play/power-quiz/*",
                        element: <Navigate to="/grow/play" replace />,
                      },
                    ],
                  },
                  // Nested UI-only gate: grow.map has no server-side guard either (same
                  // reasoning as save.community above - selfAssessmentResults / smartAnalysisResults
                  // / growRanking also back Grow's dashboard and the self-assessment result
                  // screen). Nested inside the outer "grow" gate: reaching Map still needs
                  // both grow (the pillar) and grow.map (this specific page).
                  {
                    element: <RequireFeature featureKey={FEATURE_CATALOG.growMap.key} />,
                    children: [
                      { path: "grow/map", element: <PowerLibraryPage /> },
                      { path: "grow/about-powers", element: <AboutPowersPage /> },
                      // Bookmarks to the Toleranz power from before it changed its genesis
                      // element. Both the power code (Cop) and the element name the
                      // self-assessment URL carries (COOPERATION) were retired, so all four
                      // shapes redirect. Each path is spelled out rather than one `Cop/*`:
                      // a splat scores below the dynamic routes underneath it, so a deep
                      // link would never reach the redirect. See RetiredGrowRedirect.
                      {
                        path: "grow/power/Cop",
                        element: <RetiredGrowRedirect to="/grow/power/Tol" />,
                      },
                      {
                        path: "grow/power/Cop/questionnaire/:questionnaireId",
                        element: (
                          <RetiredGrowRedirect to="/grow/power/Tol/questionnaire/:questionnaireId" />
                        ),
                      },
                      { path: "grow/power/:abbr", element: <PowerDetailPage /> },
                      { path: "grow/power/:abbr/questionnaire/:questionnaireId", element: <QuestionnaireAssessmentPage /> },
                      { path: "grow/COOPERATION", element: <RetiredGrowRedirect to="/grow/TOLERANCE" /> },
                      {
                        path: "grow/COOPERATION/result",
                        element: <RetiredGrowRedirect to="/grow/TOLERANCE/result" />,
                      },
                      { path: "grow/:elementId", element: <ElementAssessmentPage /> },
                      { path: "grow/:elementId/result", element: <ElementResultPage /> },
                    ],
                  },
                ],
              },

              // Match, gated on the "match" feature key.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.match.key} />,
                children: [
                  { path: "match", element: <MatchPage /> },
                  {
                    path: "match/new",
                    element: <Navigate to="/match" replace />,
                  },
                  { path: "match/:matchId", element: <MatchDetailPage /> },
                ],
              },

              // Share, gated on the "share" feature key. UI-only (242-access-model): genesis has
              // no server-side guard for it, sharedItems/sharedItem answer for the caller's own
              // and their company's items through the same query a stranger's public items come
              // back through, so a real guard would hide the caller's own content too. genesis
              // still enforces per-item sight regardless; this only keeps the pillar's routes out
              // of reach when the whitelist doesn't cover it.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.share.key} />,
                children: [
                  { path: "share", element: <SharePage /> },
                  { path: "share/create", element: <ShareCreatePage /> },
                  { path: "share/edit/:itemId", element: <ShareCreatePage /> },
                  // Share discovery lives in the extended search now (/extended-search
                  // ?type=SHARE). /share/discover/:id is the item detail viewer.
                  { path: "share/discover/:id", element: <ShareItemDetailPage /> },
                  { path: "share/mine", element: <ShareMinePage /> },
                  // The same page for somebody else's items, not a second one beside it: the
                  // grid, the type chips, the sorting and the paging are the same question
                  // asked about a different author, and two components would answer it two
                  // ways within a release. ShareMinePage reads `:personId` to tell the modes
                  // apart. Under the share feature key like the own page, and gated per item
                  // by genesis regardless of what this route lets through.
                  { path: "share/person/:personId", element: <ShareMinePage /> },
                ],
              },

              // Search, gated on the "search" feature key.
              {
                element: <RequireFeature featureKey={FEATURE_CATALOG.search.key} />,
                children: [
                  { path: "extended-search", element: <ExtendedSearchPage /> },
                ],
              },
              // Initial persisted Project prototype. The route inherits authentication
              // but has no FEATURE_CATALOG gate yet; see docs/project-prototype.md.
              // Ein angelegtes Projekt öffnen. Dieselbe Seite, nur mit Id: sie lädt
              // Name und Beschreibung aus genesis, statt mit der Anlegen-Maske zu starten.
              { path: "project/:projectId", element: <ProjectMockPage /> },
              // Personal account settings: any authenticated user (parent requireAuth).
              { path: "settings", element: <UserSettingsPage /> },
              // System settings: gated to users with a system-settings privilege.
              // genesis still enforces every write server-side; requireSystemAccess
              // only keeps the page out of unprivileged hands.
              {
                path: "admin/settings",
                loader: requireSystemAccess,
                element: <SettingsPage />,
              },
            ],
          },
        ],
      },

      // 404 catch-all
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
    {
      basename:
        cfg.basePath === "/" ? undefined : cfg.basePath.replace(/\/$/, ""),
    },
  );
}
